import React, { useCallback, useState, useEffect } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import './autocomplete.module.css';
import { Supplier } from 'types/supplier';
import { toast } from 'react-toastify';
import { api } from 'services/api';
import SupplierItem from './SupplierItem';

let timer: NodeJS.Timeout;

interface AutocompleteFromProps {
  onSelect(value: Supplier | null): void;
  label: string;
  placeholder: string;
  selected: Supplier | null;
  helperText?: string;
  error?: boolean;
  ref?: React.RefObject<HTMLInputElement | null>;
}

const SuppliersAutoComplete: React.FC<AutocompleteFromProps> = ({
  onSelect,
  label,
  placeholder,
  selected,
  helperText = '',
  error = false,
  ref,
}) => {
  const [loading, setLoading] = useState(false);
  const [filtered, setFiltered] = useState<Supplier[]>([]);
  const [options, setOptions] = useState<Supplier[]>([]);

  const handleSelect = useCallback(
    (option: Supplier) => {
      onSelect(option);
      setFiltered([]);
    },
    [onSelect],
  );

  useEffect(() => {
    setFiltered(options);
  }, [options]);

  const findSuppliers = useCallback(async (term: string) => {
    setLoading(true);
    api
      .get('/suppliers', { params: { rows: 200, page: 1, name: term, active: 1 } })
      .then(response => {
        setOptions(response.data.items);
      })
      .catch(err => {
        toast.error('Ocorreu um erro ao buscar os fornecedores');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  function handleChange(value: string) {
    clearTimeout(timer);

    if (value.length < 2) {
      return;
    }

    timer = setTimeout(() => findSuppliers(value), 400);
  }

  return (
    <Autocomplete
      value={selected}
      options={filtered}
      noOptionsText="Nenhum fornecedor encontrado"
      getOptionLabel={option => option.company_name}
      onChange={(event, newValue) => onSelect(newValue)}
      renderOption={(props, option) => (
        <SupplierItem {...props} key={option.id} handleSelect={handleSelect} option={option} selected={selected} />
      )}
      renderInput={params => (
        <TextField
          {...params}
          onBlur={() => setFiltered([])}
          onChange={e => handleChange(e.target.value)}
          label={label}
          placeholder={placeholder}
          error={error}
          inputRef={ref}
          helperText={helperText || 'Digite 2 ou mais caracteres'}
          fullWidth
        />
      )}
    />
  );
};

export default SuppliersAutoComplete;
